import {
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import css from "../../css/modules/toolbar.module.scss";
import { useCallback, useEffect, useMemo, useState } from "react";
import { translate } from "../../utils/translate";
import { getWorkingTime } from "../../utils/ganttUtils";

const indicatorItems = [
  {
    text: "Early Dates",
    indicator: "earlyDates",
    checked: true,
  },
  {
    text: "Constraint Date",
    indicator: "constraintDate",
    checked: true,
  },
  {
    text: "Late Dates",
    indicator: "lateDates",
    checked: true,
  },
  {
    text: "Deadline Date",
    indicator: "deadlineDate",
    checked: true,
  },
];

const features = [
  {
    text: "Draw dependencies",
    feature: "dependencies",
    checked: false,
  },
  {
    text: "Task labels",
    feature: "labels",
    checked: true,
  },
  {
    text: "Project lines",
    feature: "projectLines",
    checked: false,
  },
  {
    text: "Highlight non-working time",
    feature: "nonWorkingTime",
    checked: false,
  },
  {
    text: "Enable cell editing",
    feature: "cellEdit",
    checked: false,
  },
  {
    text: "Show baselines",
    feature: "baselines",
    checked: false,
  },
  {
    text: "Show rollup",
    feature: "rollups",
    checked: false,
  },
  {
    text: "Show progress line",
    feature: "progressLine",
    checked: false,
  },
  {
    text: "Hide schedule",
    feature: "subGrid",
    checked: false,
  },
  {
    text: "Hide weekends",
    feature: "hideWeekends",
    checked: false,
  },
];

export function Features({ api }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [initialIndicators, setInitialIndicators] = useState(indicatorItems);

  useEffect(() => {
    setInitialIndicators(
      !api.gantt?.features?.indicators?.items?.["previousDependencyEndDate"]
        ? indicatorItems
        : [
            ...indicatorItems,
            {
              text: "Next dependency StartDate",

              indicator: "nextDependencyStartDate",
              checked: true,
            },
            {
              text: "Prev dependency EndDate",
              indicator: "previousDependencyEndDate",
              checked: true,
            },
          ]
    );
  }, [api.gantt]);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const items = useMemo(() => {
    const gantt = api.gantt;
    if (!open || !gantt) {
      return [];
    }
    return features.filter((i) => {
      if (!gantt) return false;
      const feature = i.feature && gantt?.features?.[i.feature];
      if (i.feature === "subGrid") {
        i.checked = gantt.subGrids.normal.collapsed;
        return true;
      } else if (i.feature === "hideWeekends") {
        i.checked = gantt?.workingTime?.fromDay === 1;
        return true;
      } else if (feature) {
        i.checked = !feature.disabled;
        return true;
      }
      return false;
    });
  }, [open, api]);
  const onFeatureChange = useCallback(
    (e, checked) => {
      const feature = api.gantt?.features?.[e.target.id];
      if (e.target.id === "subGrid") {
        const subGrid = api.gantt?.subGrids?.normal;
        subGrid.collapsed = checked;
        return;
      }
      if (e.target.id === "hideWeekends") {
        api.gantt.workingTime = getWorkingTime(checked);
        return;
      }
      if (feature) {
        feature.disabled = !checked;
      }
    },
    [api]
  );
  const onIndicatorChange = useCallback(
    (e, checked) => {
      api.gantt.features.indicators.items[e.target.id] = checked;
    },
    [api]
  );

  const indicators = useMemo(() => {
    if (!open || !api.gantt) {
      return [];
    }
    return initialIndicators.map((i) => {
      return {
        ...i,
        checked: !!api.gantt.features.indicators.items[i.indicator],
      };
    });
  }, [api, open, initialIndicators]);
  return (
    <>
      <Tooltip title={translate("Toggle features")}>
        <Button
          className={css.iconBtn}
          size="small"
          aria-describedby={"feature-pop"}
          onClick={handleClick}
          data-testid="tb_features"
        >
          <span className="ico icon-sort-amount-asc" />
        </Button>
      </Tooltip>
      <Popover
        id={"feature-pop"}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <div className={css.featuresPopover} data-testid="tb_features_popover">
          <Typography variant="caption">{translate("Features")}</Typography>
          <Divider />
          <br />
          {items.map((i) => {
            return (
              <FormControl className={css.formControl} fullWidth key={i.feature}>
                <FormControlLabel
                  value="start"
                  control={
                    <Checkbox color="primary" id={i.feature} defaultChecked={i.checked} onChange={onFeatureChange} />
                  }
                  label={translate(i.text)}
                  labelPlacement="end"
                />
              </FormControl>
            );
          })}
          <br />
          <br />
          <Typography variant="caption">{translate("Indicators")}</Typography>
          <Divider />
          {indicators.map((i) => {
            return (
              <FormControl className={css.formControl} fullWidth key={i.indicator}>
                <FormControlLabel
                  value="start"
                  control={
                    <Checkbox
                      color="primary"
                      id={i.indicator}
                      defaultChecked={i.checked}
                      onChange={onIndicatorChange}
                    />
                  }
                  label={translate(i.text)}
                  labelPlacement="end"
                />
              </FormControl>
            );
          })}
        </div>
      </Popover>
    </>
  );
}
